import React, { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Modal, Button, TextArea } from "@/components/ui";
import { toast } from "sonner";

// Post-win prompt + testimonial consent. Mounted once in the caregiver Layout.
// Re-checks for a pending prompt on the `kb:check-feedback` window event, which
// Claims dispatches right after a claim is marked resolved.
export function WinFeedback() {
  const [prompt, setPrompt] = useState(null);
  const [consent, setConsent] = useState(null);
  const [stage, setStage] = useState("question"); // question | consent | done
  const [answer, setAnswer] = useState("");
  const [busy, setBusy] = useState(false);

  const check = useCallback(async () => {
    try {
      const r = await api.feedbackPending();
      if (r?.prompt) { setPrompt(r.prompt); setConsent(r.consent); setStage("question"); setAnswer(""); }
    } catch { /* silent — feedback is non-critical */ }
  }, []);

  useEffect(() => {
    check();
    const h = () => check();
    window.addEventListener("kb:check-feedback", h);
    return () => window.removeEventListener("kb:check-feedback", h);
  }, [check]);

  if (!prompt) return null;
  const close = () => setPrompt(null);

  const submitAnswer = async () => {
    if (!answer.trim()) return;
    setBusy(true);
    try { const r = await api.feedbackAnswer(prompt.id, answer.trim()); setConsent(r?.consent || consent); setStage("consent"); }
    catch { toast.error("Could not save that. Please try again."); }
    finally { setBusy(false); }
  };
  const dismiss = async () => { try { await api.feedbackDismiss(prompt.id); } catch { /* ignore */ } close(); };
  const choose = async (choice) => {
    setBusy(true);
    try { await api.feedbackConsent(prompt.id, choice); setStage("done"); }
    catch { toast.error("Could not save your choice."); }
    finally { setBusy(false); }
  };

  const title = stage === "consent" ? "One more thing" : stage === "done" ? "Thank you" : "You got a win!";
  return (
    <Modal open onClose={stage === "done" ? close : dismiss} title={title} testid="win-feedback-modal">
      {stage === "question" && (
        <div>
          <p className="text-[15px] text-ink mb-3">{prompt.question}</p>
          <TextArea data-testid="win-answer-input" value={answer} onChange={(e) => setAnswer(e.target.value)}
            maxLength={2000} placeholder="A sentence or two is perfect." rows={4} />
          <div className="flex gap-2 justify-end">
            <Button variant="ghost" data-testid="win-dismiss" onClick={dismiss}>Not now</Button>
            <Button data-testid="win-submit" onClick={submitAnswer} loading={busy} disabled={!answer.trim()}>Share</Button>
          </div>
        </div>
      )}
      {stage === "consent" && consent && (
        <div>
          <p className="font-semibold text-ink">{consent.question}</p>
          <p className="text-sm text-subtle mt-2">{consent.usage}</p>
          <div className="mt-4 space-y-2">
            {consent.options.map((o) => (
              <Button key={o.value} variant="secondary" className="w-full justify-start"
                data-testid={`win-consent-${o.value}`} onClick={() => choose(o.value)} disabled={busy}>
                {o.label}
              </Button>
            ))}
          </div>
        </div>
      )}
      {stage === "done" && (
        <div>
          <p className="text-[15px] text-ink">Thank you — this genuinely helps other families. You can change your mind any time from Account &amp; settings.</p>
          <div className="flex justify-end mt-4"><Button data-testid="win-done" onClick={close}>Done</Button></div>
        </div>
      )}
    </Modal>
  );
}
